import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Lock, Target, Users, RefreshCcw, FileText, Shield, Award, Brain } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease, delay },
});
const stagger = (i) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-30px" },
  transition: { duration: 0.55, ease, delay: i * 0.09 },
});

const STEPS = [
  { num: "01", title: "Share Your Goals", desc: "Tell us your target roles, experience, preferred companies, and work preferences. We build your profile." },
  { num: "02", title: "Profile Optimization", desc: "We improve your resume, LinkedIn, and cover letter before the first application goes out." },
  { num: "03", title: "Strategic Applications", desc: "We research openings and submit through official company portals, aligned with your direction." },
  { num: "04", title: "Weekly Review", desc: "We share what was applied, gather your feedback, and stay aligned every week." },
  { num: "05", title: "Refine & Improve", desc: "Strategy adjusts based on market signals and results. The process gets smarter over time." },
];

const ROLES = ["Machine Learning Engineering", "AI Engineering", "Software Engineering", "Cybersecurity", "Data Engineering", "ML Ops / Platform", "Cloud & Infrastructure", "Backend Engineering"];

const DIFF = [
  { icon: Lock, title: "Official Portals Only", desc: "Every application goes through official company portals. No shortcuts." },
  { icon: Target, title: "Strategic, Not Random", desc: "Applications aligned with your goals — not sent blindly to everything." },
  { icon: Users, title: "Limited Slots", desc: "Deliberately capped intake to ensure focused attention per profile." },
  { icon: RefreshCcw, title: "Weekly Feedback Loop", desc: "Built-in iteration every week, based on real results." },
  { icon: FileText, title: "Full Profile Optimization", desc: "Resume, LinkedIn, cover letters — all included, not upsells." },
  { icon: Shield, title: "Honest Communication", desc: "No false promises. You know exactly what's happening at all times." },
  { icon: Award, title: "Strategy Refund Guarantee", desc: "Dissatisfied with our strategy? Full refund. No debate." },
  { icon: Brain, title: "Deep Tech Focus", desc: "We know ML, AI, and security roles. No generalists on our team." },
];

export default function HowItWorksSection() {
  return (
    <>
      {/* ── HOW IT WORKS ── */}
      <section className="py-28 md:py-36 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp()} className="text-center max-w-2xl mx-auto mb-16">
            <p className="section-label-orange mb-3">Process</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-ink mb-5">A Process Built for Serious Execution</h2>
            <p className="text-lg text-ink-secondary">Five clear steps. Systematic execution on your behalf.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {STEPS.map((s, i) => (
              <motion.div key={s.num} {...stagger(i)} className="group bg-surface-raised rounded-3xl p-7 hover:bg-white hover:shadow-[0_8px_30px_rgba(255,82,0,0.10)] hover:-translate-y-1 transition-all duration-400">
                <span className={`text-4xl font-display font-bold text-brand/25 group-hover:text-brand/50 transition-colors mb-4 block animate-float-d${i}`}>{s.num}</span>
                <h3 className="text-ink font-medium text-base mb-2">{s.title}</h3>
                <p className="text-ink-secondary text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp(0.4)} className="text-center mt-10">
            <Link to="/how-it-works" className="inline-flex items-center gap-1.5 text-brand hover:text-brand-hover text-sm font-medium transition-colors">
              Learn more about our process <ChevronRight size={15} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── COMMUNITY BANNER ── */}
      <section className="py-6 bg-[#25D366]" data-testid="community-banner">
        <div className="max-w-5xl mx-auto px-6">
          <a href="https://chat.whatsapp.com/FXDjBtdoTOE2mOHjBcSHmT" target="_blank" rel="noopener noreferrer"
            className="flex flex-col sm:flex-row items-center justify-center gap-3 text-center sm:text-left group">
            <span className="flex items-center gap-2 text-white font-medium text-base">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Join our WhatsApp Community
            </span>
            <span className="text-white/80 text-sm">Stay ahead with daily job market updates, hiring trends, and strategy tips —</span>
            <span className="text-white font-medium text-sm underline underline-offset-2 group-hover:text-white/90 transition-colors">Join free →</span>
          </a>
        </div>
      </section>

      {/* ── WHO WE HELP ── */}
      <section className="py-28 md:py-36 bg-surface-raised" data-testid="who-we-help-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp()} className="text-center max-w-2xl mx-auto mb-14">
            <p className="section-label-orange mb-3">Who We Help</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-ink mb-5">Built for Serious Tech Candidates</h2>
            <p className="text-lg text-ink-secondary leading-relaxed">We work with tech professionals in India and the US targeting high-demand roles at top global companies.</p>
          </motion.div>
          <motion.div {...fadeUp(0.1)} className="flex flex-wrap justify-center gap-3 mb-12">
            {ROLES.map((role) => (
              <span key={role} className="px-5 py-2.5 rounded-full bg-white border border-black/5 text-ink text-sm font-medium shadow-sm hover:border-brand/30 hover:bg-brand-light hover:text-brand transition-all duration-200 cursor-default">{role}</span>
            ))}
          </motion.div>
          <motion.div {...fadeUp(0.2)} className="text-center">
            <Link to="/who-we-help" className="inline-flex items-center gap-1.5 text-brand hover:text-brand-hover text-sm font-medium transition-colors">
              See eligibility details <ChevronRight size={15} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── WHY US ── */}
      <section className="py-28 md:py-36 bg-white" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp()} className="text-center max-w-2xl mx-auto mb-16">
            <p className="section-label-orange mb-3">Why ApplyStrat</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-ink mb-5">Not Mass Applying.<br />Serious Execution.</h2>
            <p className="text-lg text-ink-secondary leading-relaxed">We're not a job application factory. We're a selective, process-driven partner.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DIFF.map((d, i) => (
              <motion.div key={d.title} {...stagger(i)} className="bg-surface-raised rounded-3xl p-7 hover:bg-white hover:shadow-[0_8px_30px_rgba(255,82,0,0.10)] hover:-translate-y-1 transition-all duration-400 group">
                <motion.div whileHover={{ scale: 1.18, rotate: -8 }} transition={{ type: "spring", stiffness: 380, damping: 14 }} className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center mb-5 shadow-sm group-hover:bg-brand-light transition-colors">
                  <d.icon size={18} className="text-ink-secondary group-hover:text-brand transition-colors" />
                </motion.div>
                <h3 className="text-ink font-medium text-sm mb-2">{d.title}</h3>
                <p className="text-ink-secondary text-xs leading-relaxed">{d.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
